import React, { FunctionComponent, memo, ReactNode, useState } from 'react'
import styles from './UserElement.module.sass'
import { User } from '../../../../responses/users'
import clsx from 'clsx'
import Ellipsis from '../../../etc/Ellipsis/Ellipsis'
import CustomArrowContainer from '../../../etc/popover/ArrowContainer/CustomArrowContainer'
import UserTasks from '../../UserTasks/UserTasks'
import { ROUTES } from '../../../../http/routes'
import { useRootDispatch, useRootSelector } from '../../../../utils/hooks'
import { taskActions } from '../../../../redux/data/task/task-actions'
import Tooltip from '../../../etc/Tooltip'
import { ImageSize } from '../../files/Image/Image'
import Popover from '../../../etc/Popover'
import SettingsIcon from 'files/settings.svg'
import { onlineSelector } from '../../../../utils/selectors'
import { urlWithParams } from '../../../../http/utils'
import { useQueries } from '../../../../utils/hooks/useQueries'

export const getUserInitials = (user?: User | null): string =>
    `${user?.surname?.[0] || ''}${user?.first_name?.[0] || ''}`.toUpperCase()

interface IconProps {
    className?: string,
    disabled?: boolean,
    user?: User | null,
    size?: ImageSize,
    system?: boolean,
    withoutRequest?: boolean,
}

export const UserIcon: FunctionComponent<IconProps> = props => {
    const dispatch = useRootDispatch()
    const initials = getUserInitials(props.user)
    const [open, setOpen] = useState(false)
    const online = useRootSelector(onlineSelector(props.user?.id))
    const {isMobile} = useQueries()

    return <Popover
        isOpen={!isMobile && open}
        positions={['bottom', 'top']}
        content={state => <CustomArrowContainer state={state}>
            <UserTasks className={styles.tasks} user={props.user}/>
        </CustomArrowContainer>}
    >
        <div
            className={clsx(
                props.className,
                'charge-' + props.user?.charge,
                styles['charge-' + ((props.user?.charge ?? 1) - 1)],
                styles.userIcon, {
                    [styles.disabled]: props.disabled,
                    [styles.system]: props.system,
                }
            )}
            onMouseEnter={() => {
                if (props.user && !props.withoutRequest) {
                    setOpen(true)
                    dispatch(taskActions.user.request(props.user.id))
                }
            }}
            onMouseLeave={() => setOpen(false)}
        >
            {
                online && <div className={styles.online}/>
            }
            {
                props.system ?
                    <SettingsIcon fill={'white'}/> :
                    props.user?.photo ?
                        <img
                            alt={initials}
                            className={styles.image}
                            src={urlWithParams({ filename: props.user.photo, size: props.size ?? 'low' })(ROUTES.AVATAR_PREVIEW)}
                        /> :
                        initials
            }
        </div>
    </Popover>
}

interface Props {
    className?: string,
    user?: User | null,
    onClick?: () => void,
    disabled?: boolean,
    onlyIcon?: boolean,
    dark?: boolean,
    noCharge?: boolean,
    simple?: boolean,
    system?: boolean,
    withoutRequest?: boolean,
    title?: string,
    iconSize?: ImageSize,
    children?: ReactNode,
}

const UserElement: FunctionComponent<Props> = props => <div
    className={clsx(
        props.className,
        styles.userPanel,
        {
            [styles.interactive]: props.onClick && !props.disabled,
            [styles.disabled]: props.disabled,
            [styles.simple]: props.simple,
            [styles.dark]: props.dark,
        }
    )}
    onClick={() => !props.disabled && props.onClick?.()}
>
    <Tooltip className={styles.content} title={{ content: props.title, disable: props.disabled, direction: 'down' }}>
        <UserIcon
            className={styles.icon}
            size={props.iconSize}
            disabled={props.disabled}
            user={props.user}
            withoutRequest={props.withoutRequest}
        />
        {
            (props.user && !props.onlyIcon) &&
            <div className={styles.info}>
                <Ellipsis className={styles.name}>
                    {props.user.name}
                </Ellipsis>
                <Ellipsis className={styles.description}>
                    {props.user.description}
                </Ellipsis>
            </div>
        }
    </Tooltip>
    {
        props.children
    }
</div>

export default memo(UserElement)